import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Loader2, Shield, Zap } from 'lucide-react';
import { useRazorpay } from '@/hooks/useRazorpay';
import { PLANS, formatPrice, SubscriptionTier } from '@/lib/pricing';

interface PaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTier: SubscriptionTier;
  billingPeriod: 'monthly' | 'annual';
  onSuccess?: () => void;
}

export function PaymentModal({
  open,
  onOpenChange,
  selectedTier,
  billingPeriod,
  onSuccess,
}: PaymentModalProps) {
  const { initiatePayment, loading } = useRazorpay();
  const plan = PLANS.find((p) => p.tier === selectedTier);

  if (!plan) return null;

  const price = billingPeriod === 'annual' ? plan.annualPrice : plan.price;
  const monthlyEquivalent = billingPeriod === 'annual' ? Math.round(plan.annualPrice / 12) : plan.price;
  const savings = billingPeriod === 'annual' ? (plan.price * 12) - plan.annualPrice : 0;

  const handlePayment = () => {
    initiatePayment({
      tier: selectedTier,
      billingPeriod,
      onSuccess: () => {
        onOpenChange(false);
        onSuccess?.();
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            Upgrade to {plan.name}
          </DialogTitle>
          <DialogDescription>
            Complete your subscription to unlock all {plan.name} features
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Plan Summary */}
          <Card className="p-4 bg-surface/50 border-primary/20">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-lg">{plan.name} Plan</h3>
                <p className="text-sm text-muted-foreground">
                  {billingPeriod === 'annual' ? 'Annual billing' : 'Monthly billing'}
                </p>
              </div>
              {billingPeriod === 'annual' && (
                <Badge variant="secondary" className="bg-success/20 text-success">
                  Save {formatPrice(savings)}
                </Badge>
              )}
            </div>

            <div className="flex items-baseline gap-1 mb-4">
              <span className="text-3xl font-bold">{formatPrice(monthlyEquivalent)}</span>
              <span className="text-muted-foreground">/month</span>
            </div>

            {billingPeriod === 'annual' && (
              <p className="text-sm text-muted-foreground mb-4">
                Billed as {formatPrice(price)} annually
              </p>
            )}

            <div className="space-y-2">
              {plan.features.slice(0, 5).map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
              {plan.features.length > 5 && (
                <p className="text-sm text-primary font-medium pl-6">
                  +{plan.features.length - 5} more features
                </p>
              )}
            </div>
          </Card>

          {/* Security Note */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Shield className="w-4 h-4" />
            <span>Secured by Razorpay. 256-bit SSL encryption.</span>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Button 
            onClick={handlePayment} 
            disabled={loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Pay {formatPrice(price)} {billingPeriod === 'annual' ? '/year' : '/month'}
              </>
            )}
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
